/**
 * pounds.ts — parse an admin-typed POUNDS amount into integer pence.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the plan-builder form takes all money in POUNDS (the CTO typed 100 into
 * the old raw-pence inputs expecting £100) while the wire stays integer pence.
 * Parsing goes through string arithmetic — never `Number(raw) * 100` — so
 * "27.99" can never float-drift to 2798.999… before rounding. Rules: decimal,
 * up to 2 decimal places (billing is whole pence), no negatives. An empty
 * input is VALID and parses to `null` — the form treats it as "not set"
 * (e.g. a discounted-term input left blank means "no discount").
 */

export type PoundsParse =
  | { ok: true; pence: number | null }
  | { ok: false; message: string };

/**
 * Parse a pounds string ("28", "28.5", "27.99") to integer pence. Empty/blank
 * → `{ok: true, pence: null}`. Negatives, non-numbers and 3+ decimal places
 * are rejected with a friendly message.
 */
export function parsePounds(raw: string): PoundsParse {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, pence: null };
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    return { ok: false, message: 'Enter a price in pounds, like 28 or 27.99.' };
  }
  const [whole, fraction = ''] = trimmed.split('.');
  if (fraction.length > 2) {
    return { ok: false, message: 'Prices go to whole pence — use at most 2 decimal places.' };
  }
  return { ok: true, pence: Number(whole) * 100 + Number(fraction.padEnd(2, '0') || '0') };
}

/**
 * Integer pence → the pounds string an input shows ("2800" → "28", "2799" →
 * "27.99"). 0 pence means "not set" (the CTO-feed placeholder rule) and maps
 * to an empty input, matching what `parsePounds` returns for it.
 */
export function poundsInputValue(pence: number): string {
  if (pence <= 0) return '';
  const whole = Math.floor(pence / 100);
  const fraction = pence % 100;
  return fraction === 0 ? String(whole) : `${whole}.${String(fraction).padStart(2, '0')}`;
}
